import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";

import { X } from "lucide-react";
import type { Tag as TagValue } from "./TagTypes";

export const tagVariants = cva(
  "transition-all border inline-flex items-center text-sm pl-2 rounded-md",
  {
    variants: {
      variant: {
        default:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:cursor-not-allowed disabled:opacity-50",
        primary:
          "bg-primary border-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50",
        destructive:
          "bg-destructive border-destructive text-destructive-foreground hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50",
      },
      size: {
        sm: "text-xs h-7",
        md: "text-sm h-8",
        lg: "text-base h-9",
        xl: "text-lg h-10",
      },
      shape: {
        default: "rounded-sm [&>button]:rounded-inherit",
        rounded: "rounded-lg [&>button]:rounded-inherit",
        square: "rounded-none [&>button]:rounded-inherit",
        pill: "rounded-full [&>button]:rounded-inherit",
      },
      borderStyle: {
        default: "border-solid",
        none: "border-none",
        dashed: "border-dashed",
        dotted: "border-dotted",
        double: "border-double",
      },
      textCase: {
        uppercase: "uppercase",
        lowercase: "lowercase",
        capitalize: "capitalize",
      },
      interaction: {
        clickable: "cursor-pointer hover:shadow-md",
        nonClickable: "cursor-default",
      },
      animation: {
        none: "",
        fadeIn: "animate-fadeIn",
        slideIn: "animate-slideIn",
        bounce: "animate-bounce",
      },
      textStyle: {
        normal: "font-normal",
        bold: "font-bold",
        italic: "italic",
        underline: "underline",
        lineThrough: "line-through",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      shape: "default",
      borderStyle: "default",
      interaction: "nonClickable",
      animation: "fadeIn",
      textStyle: "normal",
    },
  },
);

type TagVisual = VariantProps<typeof tagVariants>;

export type TagChipProps = {
  tagObj: TagValue;
  variant: TagVisual["variant"];
  size: TagVisual["size"];
  shape: TagVisual["shape"];
  borderStyle: TagVisual["borderStyle"];
  textCase: TagVisual["textCase"];
  interaction: TagVisual["interaction"];
  animation: TagVisual["animation"];
  textStyle: TagVisual["textStyle"];
  onRemoveTag: (id: string) => void;
  isActiveTag?: boolean;
  disabled?: boolean;
  direction?: "row" | "column";
  onTagClick?: (tag: TagValue) => void;
  draggable?: boolean;
};

export const TagChip: React.FC<TagChipProps> = ({
  tagObj,
  direction,
  draggable,
  onTagClick,
  onRemoveTag,
  variant,
  size,
  shape,
  borderStyle,
  textCase,
  interaction,
  animation,
  textStyle,
  isActiveTag,
  disabled,
}) => {
  return (
    <span
      key={tagObj.id}
      draggable={draggable}
      className={cn(
        tagVariants({
          variant,
          size,
          shape,
          borderStyle,
          textCase,
          interaction,
          animation,
          textStyle,
        }),
        {
          "justify-between w-full": direction === "column",
          "cursor-pointer": draggable,
          "ring-ring ring-offset-2 ring-2 ring-offset-background": isActiveTag,
        },
      )}
      onClick={() => onTagClick?.(tagObj)}
    >
      {tagObj.text}
      <Button
        type="button"
        variant="ghost"
        size="xs"
        onClick={(e) => {
          e.stopPropagation();
          onRemoveTag(tagObj.id);
        }}
        disabled={disabled}
        className={cn("py-1 px-3 h-full hover:bg-transparent")}
      >
        <X />
      </Button>
    </span>
  );
};
